import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Gavel, Scale, Users, MessageSquare, Vote, Loader2, Sparkles, AlertTriangle, Send, Mic, MicOff, Square, Brain, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';

type GamePhase = 'landing' | 'setup' | 'scenario' | 'debate' | 'defense' | 'interrogation' | 'analysis' | 'verdict' | 'end';

interface Player {
  id: string;
  name: string;
  vote?: 'guilty' | 'innocent';
}

interface Scenario {
  title: string;
  situation: string;
  accusedRole: string;
  dilemma: string;
  context: string;
}

interface ProsecutorQuestion {
  question: string;
  followUp?: string;
}

interface ResponseAnalysis {
  score: number;
  feedback: string;
}

interface DefenseAnalysis {
  scores: {
    clarte: number;
    argumentation: number;
    emotion: number;
    coherence: number;
    conviction: number;
  };
  scoreGlobal: number;
  verdict: 'coupable' | 'innocent' | 'indécis';
  analyse: string;
  pointsForts: string[];
  pointsFaibles: string[];
}

const fallbackScenarios: Scenario[] = [
  {
    title: "Le Vol Nécessaire",
    situation: "Un père de famille a volé des médicaments coûteux pour sauver son enfant malade.",
    accusedRole: "Le père accusé",
    dilemma: "Peut-on justifier un crime pour sauver une vie ?",
    context: "La pharmacie refuse tout crédit et l'enfant mourra sans traitement dans 48h."
  },
  {
    title: "Le Secret Mortel",
    situation: "Un médecin a révélé le dossier médical d'un patient qui cachait une maladie contagieuse.",
    accusedRole: "Le médecin accusé",
    dilemma: "Le secret médical doit-il céder face au danger public ?",
    context: "Le patient refusait de prévenir ses proches malgré les risques de contamination."
  },
  {
    title: "La Dénonciation",
    situation: "Un employé a dénoncé les pratiques illégales de son entreprise aux médias.",
    accusedRole: "Le lanceur d'alerte",
    dilemma: "La loyauté envers son employeur prime-t-elle sur l'intérêt général ?",
    context: "L'entreprise polluait secrètement une rivière, causant des maladies dans le village voisin."
  }
];

const fallbackQuestions: ProsecutorQuestion[] = [
  { question: "Connaissiez-vous les conséquences de vos actes avant d'agir ?", followUp: "Et malgré cela, vous avez choisi de continuer ?" },
  { question: "Avez-vous envisagé d'autres solutions avant de commettre cet acte ?", followUp: "Pourquoi ne pas avoir cherché de l'aide ?" },
  { question: "Regrettez-vous votre décision aujourd'hui ?", followUp: "Referiez-vous la même chose ?" }
];

export default function Tribunal() {
  const [phase, setPhase] = useState<GamePhase>('landing');
  const [players, setPlayers] = useState<Player[]>([]);
  const [playerName, setPlayerName] = useState('');
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [accusedPlayer, setAccusedPlayer] = useState<Player | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<ProsecutorQuestion | null>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingQuestion, setIsGeneratingQuestion] = useState(false);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [defenseText, setDefenseText] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [defenseAnalysis, setDefenseAnalysis] = useState<DefenseAnalysis | null>(null);
  
  // Interrogation recording states
  const [interrogationResponses, setInterrogationResponses] = useState<{text: string, analysis: ResponseAnalysis | null}[]>([]);
  const [currentResponseText, setCurrentResponseText] = useState('');
  const [isRecordingResponse, setIsRecordingResponse] = useState(false);
  const [responseRecordingTime, setResponseRecordingTime] = useState(0);
  const [isTranscribingResponse, setIsTranscribingResponse] = useState(false);
  const [isAnalyzingResponse, setIsAnalyzingResponse] = useState(false);
  const [currentResponseAnalysis, setCurrentResponseAnalysis] = useState<ResponseAnalysis | null>(null);
  
  // TTS states
  const [isSpeakingQuestion, setIsSpeakingQuestion] = useState(false);
  const [prosecutorVoiceEnabled, setProsecutorVoiceEnabled] = useState(true);
  const questionAudioRef = useRef<HTMLAudioElement | null>(null);
  const questionAudioUrlRef = useRef<string | null>(null);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  const { toast } = useToast();

  const addPlayer = () => {
    if (playerName.trim() && players.length < 8) {
      setPlayers([...players, { id: crypto.randomUUID(), name: playerName.trim() }]);
      setPlayerName('');
    }
  };

  const removePlayer = (id: string) => {
    setPlayers(players.filter(p => p.id !== id));
  };

  const generateScenario = async () => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-tribunal-scenario', {
        body: { playerCount: players.length }
      });

      if (error) throw error;

      if (data && data.title) {
        setScenario(data);
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('Error generating scenario:', error);
      const fallback = fallbackScenarios[Math.floor(Math.random() * fallbackScenarios.length)];
      setScenario(fallback);
      toast({
        title: "Mode hors-ligne",
        description: "Scénario classique utilisé",
        variant: "default"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const startGame = async () => {
    await generateScenario();
    const randomIndex = Math.floor(Math.random() * players.length);
    setAccusedPlayer(players[randomIndex]);
    setPhase('scenario');
  };

  // TTS functions
  const speakQuestion = useCallback(async (text: string) => {
    if (!prosecutorVoiceEnabled) return;
    
    // Stop any currently playing audio
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current = null;
    }
    if (questionAudioUrlRef.current) {
      URL.revokeObjectURL(questionAudioUrlRef.current);
      questionAudioUrlRef.current = null;
    }
    
    setIsSpeakingQuestion(true);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-tts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ 
            text, 
            voiceType: 'pirate' // Deep, authoritative voice for the prosecutor
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`TTS request failed: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      questionAudioUrlRef.current = audioUrl;

      const audio = new Audio(audioUrl);
      questionAudioRef.current = audio;

      audio.onended = () => {
        setIsSpeakingQuestion(false);
      };

      audio.onerror = () => {
        setIsSpeakingQuestion(false);
      };

      await audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeakingQuestion(false);
    }
  }, [prosecutorVoiceEnabled]);

  const stopSpeakingQuestion = useCallback(() => {
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current = null;
      setIsSpeakingQuestion(false);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
      }
      if (questionAudioUrlRef.current) {
        URL.revokeObjectURL(questionAudioUrlRef.current);
      }
    };
  }, []);

  const generateProsecutorQuestion = async () => {
    if (!scenario || !accusedPlayer) return;
    
    // Stop any playing audio before generating new question
    stopSpeakingQuestion();
    
    setIsGeneratingQuestion(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-prosecutor-question', {
        body: { 
          scenario: scenario,
          accusedName: accusedPlayer.name,
          questionNumber: questionIndex + 1
        }
      });

      if (error) throw error;

      if (data && data.question) {
        setCurrentQuestion(data);
        // Auto-speak the question after generating
        if (prosecutorVoiceEnabled) {
          speakQuestion(data.question);
        }
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      console.error('Error generating question:', error);
      const fallback = fallbackQuestions[questionIndex % fallbackQuestions.length];
      setCurrentQuestion(fallback);
      // Also speak fallback questions
      if (prosecutorVoiceEnabled) {
        speakQuestion(fallback.question);
      }
    } finally {
      setIsGeneratingQuestion(false);
    }
  };

  const startDefensePhase = () => {
    setPhase('defense');
    setDefenseText('');
    setDefenseAnalysis(null);
  };

  // Generic recording functions
  const startRecordingGeneric = useCallback(async (
    setRecordingState: (val: boolean) => void,
    setTimeState: (val: number | ((prev: number) => number)) => void
  ) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(1000);
      setRecordingState(true);
      setTimeState(0);
      
      timerRef.current = setInterval(() => {
        setTimeState(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Erreur micro",
        description: "Impossible d'accéder au microphone",
        variant: "destructive"
      });
    }
  }, [toast]);

  const stopRecordingGeneric = useCallback(async (
    setRecordingState: (val: boolean) => void
  ): Promise<Blob> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve(new Blob());
        return;
      }
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        resolve(audioBlob);
      };
      
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      setRecordingState(false);
    });
  }, []);

  // Defense recording
  const startRecording = useCallback(() => {
    startRecordingGeneric(setIsRecording, setRecordingTime);
  }, [startRecordingGeneric]);

  const handleStopAndTranscribe = async () => {
    setIsTranscribing(true);
    
    try {
      const audioBlob = await stopRecordingGeneric(setIsRecording);
      
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('No audio recorded');
      }
      
      const formData = new FormData();
      formData.append('audio', audioBlob, 'defense.webm');
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe-defense`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: formData,
        }
      );
      
      if (!response.ok) {
        throw new Error('Transcription failed');
      }
      
      const data = await response.json();
      
      if (data.text) {
        setDefenseText(data.text);
        toast({
          title: "Transcription réussie",
          description: "Votre plaidoirie a été transcrite",
        });
      } else {
        throw new Error('No transcription received');
      }
      
    } catch (error) {
      console.error('Error transcribing:', error);
      toast({
        title: "Erreur de transcription",
        description: "Impossible de transcrire l'audio. Réessayez.",
        variant: "destructive"
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  const analyzeDefense = async () => {
    if (!defenseText || !scenario || !accusedPlayer) return;
    
    setIsAnalyzing(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('analyze-defense', {
        body: {
          defenseText,
          scenario,
          accusedName: accusedPlayer.name
        }
      });
      
      if (error) throw error;
      
      if (data && data.scoreGlobal !== undefined) {
        setDefenseAnalysis(data);
        setPhase('analysis');
      } else {
        throw new Error('Invalid analysis response');
      }
      
    } catch (error) {
      console.error('Error analyzing defense:', error);
      toast({
        title: "Erreur d'analyse",
        description: "Impossible d'analyser la plaidoirie",
        variant: "destructive"
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Interrogation recording
  const startRecordingResponse = useCallback(() => {
    setCurrentResponseText('');
    setCurrentResponseAnalysis(null);
    startRecordingGeneric(setIsRecordingResponse, setResponseRecordingTime);
  }, [startRecordingGeneric]);

  const handleStopAndTranscribeResponse = async () => {
    setIsTranscribingResponse(true);
    
    try {
      const audioBlob = await stopRecordingGeneric(setIsRecordingResponse);
      
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('No audio recorded');
      }
      
      const formData = new FormData();
      formData.append('audio', audioBlob, 'response.webm');
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe-defense`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: formData,
        }
      );
      
      if (!response.ok) {
        throw new Error('Transcription failed');
      }
      
      const data = await response.json();
      
      if (data.text) {
        setCurrentResponseText(data.text);
        // Auto-analyze the response
        await analyzeResponse(data.text);
      } else {
        throw new Error('No transcription received');
      }
      
    } catch (error) {
      console.error('Error transcribing response:', error);
      toast({
        title: "Erreur de transcription",
        description: "Réponse non enregistrée",
        variant: "destructive"
      });
    } finally {
      setIsTranscribingResponse(false);
    }
  };

  const analyzeResponse = async (responseText: string) => {
    if (!currentQuestion || !scenario || !accusedPlayer) return;
    
    setIsAnalyzingResponse(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('analyze-response', {
        body: {
          responseText,
          question: currentQuestion.question,
          scenario,
          accusedName: accusedPlayer.name
        }
      });
      
      if (error) throw error;
      
      if (data && data.score !== undefined) {
        setCurrentResponseAnalysis(data);
      }
      
    } catch (error) {
      console.error('Error analyzing response:', error);
      // Fallback analysis
      setCurrentResponseAnalysis({
        score: 50,
        feedback: "Analyse non disponible"
      });
    } finally {
      setIsAnalyzingResponse(false);
    }
  };

  const startInterrogation = async () => {
    setPhase('interrogation');
    setQuestionIndex(0);
    setInterrogationResponses([]);
    setCurrentResponseText('');
    setCurrentResponseAnalysis(null);
    await generateProsecutorQuestion();
  };

  const nextQuestion = async () => {
    // Save current response
    if (currentResponseText) {
      setInterrogationResponses(prev => [...prev, {
        text: currentResponseText,
        analysis: currentResponseAnalysis
      }]);
    }
    
    // Reset for next question
    setCurrentResponseText('');
    setCurrentResponseAnalysis(null);
    setResponseRecordingTime(0);
    
    if (questionIndex < 2) {
      setQuestionIndex(prev => prev + 1);
      await generateProsecutorQuestion();
    } else {
      setPhase('verdict');
    }
  };

  const castVote = (playerId: string, vote: 'guilty' | 'innocent') => {
    setPlayers(players.map(p => 
      p.id === playerId ? { ...p, vote } : p
    ));
  };

  const calculateVerdict = () => {
    const votes = players.filter(p => p.id !== accusedPlayer?.id);
    const guiltyVotes = votes.filter(p => p.vote === 'guilty').length;
    const innocentVotes = votes.filter(p => p.vote === 'innocent').length;
    
    // Calculate total AI score from defense + interrogation responses
    let totalAiScore = 0;
    let scoreCount = 0;
    
    if (defenseAnalysis) {
      totalAiScore += defenseAnalysis.scoreGlobal;
      scoreCount++;
    }
    
    interrogationResponses.forEach(r => {
      if (r.analysis) {
        totalAiScore += r.analysis.score;
        scoreCount++;
      }
    });
    
    const averageAiScore = scoreCount > 0 ? totalAiScore / scoreCount : 50;
    
    // AI influence based on average score
    let aiInfluence = 0;
    if (averageAiScore >= 75) {
      aiInfluence = 2;
    } else if (averageAiScore >= 60) {
      aiInfluence = 1;
    } else if (averageAiScore <= 35) {
      aiInfluence = -2;
    } else if (averageAiScore <= 45) {
      aiInfluence = -1;
    }
    
    const adjustedInnocentVotes = innocentVotes + (aiInfluence > 0 ? aiInfluence : 0);
    const adjustedGuiltyVotes = guiltyVotes + (aiInfluence < 0 ? Math.abs(aiInfluence) : 0);
    
    return { 
      guiltyVotes, 
      innocentVotes, 
      aiInfluence,
      averageAiScore: Math.round(averageAiScore),
      isGuilty: adjustedGuiltyVotes > adjustedInnocentVotes 
    };
  };

  const endGame = () => {
    setPhase('end');
  };

  const resetGame = () => {
    setPhase('landing');
    setPlayers([]);
    setScenario(null);
    setAccusedPlayer(null);
    setCurrentQuestion(null);
    setQuestionIndex(0);
    setDefenseText('');
    setDefenseAnalysis(null);
    setInterrogationResponses([]);
    setCurrentResponseText('');
    setCurrentResponseAnalysis(null);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 70) return 'bg-green-500/20 border-green-500/30';
    if (score >= 50) return 'bg-yellow-500/20 border-yellow-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-600/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Retour</span>
        </Link>
        <div className="flex items-center gap-2">
          <Gavel className="w-6 h-6 text-amber-400" />
          <span className="font-bold text-amber-400">Le Tribunal</span>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 container mx-auto px-4 py-8 max-w-2xl">
        
        {/* Landing */}
        {phase === 'landing' && (
          <div className="text-center space-y-8 animate-fade-in">
            <div className="space-y-4">
              <Scale className="w-24 h-24 mx-auto text-amber-400 animate-pulse" />
              <h1 className="text-5xl font-black tracking-tight bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                LE TRIBUNAL
              </h1>
              <p className="text-slate-400 text-lg max-w-md mx-auto">
                Dilemmes moraux, débats passionnés et verdicts impitoyables. L'IA est votre procureur et juge.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto text-left">
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <AlertTriangle className="w-8 h-8 text-amber-400 mb-2" />
                <h3 className="font-bold text-amber-300">Scénarios IA</h3>
                <p className="text-sm text-slate-400">Dilemmes uniques générés</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Mic className="w-8 h-8 text-amber-400 mb-2" />
                <h3 className="font-bold text-amber-300">Enregistrement</h3>
                <p className="text-sm text-slate-400">Plaidoirie & réponses</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Brain className="w-8 h-8 text-amber-400 mb-2" />
                <h3 className="font-bold text-amber-300">Analyse IA</h3>
                <p className="text-sm text-slate-400">L'IA juge vos discours</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                <Vote className="w-8 h-8 text-amber-400 mb-2" />
                <h3 className="font-bold text-amber-300">Verdict</h3>
                <p className="text-sm text-slate-400">L'IA influence le vote</p>
              </div>
            </div>

            <Button 
              onClick={() => setPhase('setup')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold px-8 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/25"
            >
              <Gavel className="w-5 h-5 mr-2" />
              Ouvrir la session
            </Button>
          </div>
        )}

        {/* Setup */}
        {phase === 'setup' && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <Users className="w-12 h-12 mx-auto text-amber-400" />
              <h2 className="text-3xl font-bold">Le Jury</h2>
              <p className="text-slate-400">3 à 8 participants pour le procès</p>
            </div>

            <div className="flex gap-2">
              <Input
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addPlayer()}
                placeholder="Nom du juré..."
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                maxLength={20}
              />
              <Button 
                onClick={addPlayer} 
                disabled={!playerName.trim() || players.length >= 8}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                Ajouter
              </Button>
            </div>

            <div className="space-y-2">
              {players.map((player, index) => (
                <div 
                  key={player.id}
                  className="flex items-center justify-between bg-slate-800/50 px-4 py-3 rounded-lg border border-slate-700/50"
                >
                  <span className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-400 font-bold text-sm">
                      {index + 1}
                    </span>
                    {player.name}
                  </span>
                  <button 
                    onClick={() => removePlayer(player.id)}
                    className="text-slate-500 hover:text-red-400 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {players.length < 3 && (
              <p className="text-center text-slate-500 text-sm">
                Minimum 3 jurés requis ({3 - players.length} restant{3 - players.length > 1 ? 's' : ''})
              </p>
            )}

            <Button 
              onClick={startGame}
              disabled={players.length < 3 || isGenerating}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold py-6 text-lg rounded-xl"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Préparation du procès...
                </>
              ) : (
                <>
                  <Scale className="w-5 h-5 mr-2" />
                  Commencer le procès
                </>
              )}
            </Button>
          </div>
        )}

        {/* Scenario */}
        {phase === 'scenario' && scenario && accusedPlayer && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm">
                <Sparkles className="w-4 h-4" />
                Affaire générée par IA
              </div>
              <h2 className="text-3xl font-bold text-amber-400">{scenario.title}</h2>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-amber-500/30 space-y-4">
              <div>
                <h3 className="text-amber-400 font-semibold mb-2">📋 Les faits</h3>
                <p className="text-slate-300">{scenario.situation}</p>
              </div>
              <div>
                <h3 className="text-amber-400 font-semibold mb-2">📍 Contexte</h3>
                <p className="text-slate-400 text-sm">{scenario.context}</p>
              </div>
              <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/30">
                <h3 className="text-red-400 font-semibold mb-2">⚖️ Le dilemme</h3>
                <p className="text-slate-300 italic">{scenario.dilemma}</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 p-4 rounded-xl border border-amber-500/30 text-center">
              <p className="text-slate-400 text-sm mb-1">L'accusé(e) désigné(e)</p>
              <p className="text-2xl font-bold text-amber-400">{accusedPlayer.name}</p>
              <p className="text-slate-400 text-sm mt-1">incarne : {scenario.accusedRole}</p>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={generateScenario}
                disabled={isGenerating}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Autre affaire"}
              </Button>
              <Button 
                onClick={() => setPhase('debate')}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Ouvrir le débat
              </Button>
            </div>
          </div>
        )}

        {/* Debate */}
        {phase === 'debate' && scenario && accusedPlayer && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <MessageSquare className="w-12 h-12 mx-auto text-amber-400" />
              <h2 className="text-3xl font-bold">Phase de Débat</h2>
              <p className="text-slate-400">Discutez du dilemme : "{scenario.dilemma}"</p>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700/50 text-center">
              <p className="text-slate-400 mb-2">L'accusé(e) peut se défendre</p>
              <p className="text-2xl font-bold text-amber-400">{accusedPlayer.name}</p>
              <p className="text-sm text-slate-500 mt-2">Les jurés posent leurs questions et débattent</p>
            </div>

            <div className="bg-amber-500/10 p-4 rounded-xl border border-amber-500/30">
              <h3 className="font-semibold text-amber-400 mb-2">💡 Rappel du dilemme</h3>
              <p className="text-slate-300 italic">"{scenario.dilemma}"</p>
            </div>

            <Button 
              onClick={startDefensePhase}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-6 text-lg rounded-xl"
            >
              <Mic className="w-5 h-5 mr-2" />
              Passer à la plaidoirie vocale
            </Button>
          </div>
        )}

        {/* Defense - Voice Recording */}
        {phase === 'defense' && scenario && accusedPlayer && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm">
                <Mic className="w-4 h-4" />
                Plaidoirie Vocale
              </div>
              <h2 className="text-3xl font-bold">Défendez-vous !</h2>
              <p className="text-slate-400">{accusedPlayer.name}, c'est votre moment</p>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-purple-500/30 text-center space-y-4">
              <p className="text-slate-300">
                Enregistrez votre plaidoirie. L'IA analysera la qualité de votre discours.
              </p>
              
              <div className="flex flex-col items-center gap-4">
                {!isRecording && !defenseText && (
                  <Button
                    onClick={startRecording}
                    disabled={isTranscribing}
                    className="w-32 h-32 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg shadow-purple-500/30"
                  >
                    <Mic className="w-12 h-12" />
                  </Button>
                )}
                
                {isRecording && (
                  <>
                    <div className="w-32 h-32 rounded-full bg-red-500/20 border-4 border-red-500 flex items-center justify-center animate-pulse">
                      <div className="text-center">
                        <Mic className="w-8 h-8 text-red-400 mx-auto mb-1" />
                        <span className="text-2xl font-mono font-bold text-red-400">{formatTime(recordingTime)}</span>
                      </div>
                    </div>
                    <Button
                      onClick={handleStopAndTranscribe}
                      className="bg-red-500 hover:bg-red-600 text-white px-8"
                    >
                      <Square className="w-4 h-4 mr-2" />
                      Arrêter et transcrire
                    </Button>
                  </>
                )}
                
                {isTranscribing && (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-12 h-12 animate-spin text-purple-400" />
                    <p className="text-purple-400">Transcription en cours...</p>
                  </div>
                )}
              </div>
              
              {!isRecording && !defenseText && !isTranscribing && (
                <p className="text-slate-500 text-sm">Appuyez pour commencer l'enregistrement</p>
              )}
            </div>

            {defenseText && (
              <div className="bg-slate-800/50 p-6 rounded-xl border border-green-500/30 space-y-4">
                <div className="flex items-center gap-2 text-green-400">
                  <Sparkles className="w-5 h-5" />
                  <h3 className="font-semibold">Transcription de votre plaidoirie</h3>
                </div>
                <p className="text-slate-300 italic">"{defenseText}"</p>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setDefenseText('');
                      setRecordingTime(0);
                    }}
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                  >
                    <MicOff className="w-4 h-4 mr-2" />
                    Réenregistrer
                  </Button>
                  <Button
                    onClick={analyzeDefense}
                    disabled={isAnalyzing}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyse...
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        Analyser par l'IA
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            <Button
              onClick={startInterrogation}
              variant="ghost"
              className="w-full text-slate-500 hover:text-slate-300"
            >
              Passer l'enregistrement →
            </Button>
          </div>
        )}

        {/* Analysis Results */}
        {phase === 'analysis' && defenseAnalysis && accusedPlayer && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm">
                <Brain className="w-4 h-4" />
                Analyse IA de la plaidoirie
              </div>
              <h2 className="text-3xl font-bold">Résultat de l'analyse</h2>
            </div>

            <div className="bg-gradient-to-r from-slate-800/50 to-purple-900/20 p-6 rounded-xl border border-purple-500/30 text-center">
              <p className="text-slate-400 mb-2">Score global de la plaidoirie</p>
              <p className={`text-6xl font-black ${getScoreColor(defenseAnalysis.scoreGlobal)}`}>
                {defenseAnalysis.scoreGlobal}/100
              </p>
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700/50 space-y-4">
              <h3 className="font-semibold text-amber-400">📊 Scores détaillés</h3>
              
              {Object.entries(defenseAnalysis.scores).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400 capitalize">{key}</span>
                    <span className={getScoreColor(value)}>{value}/100</span>
                  </div>
                  <Progress value={value} className="h-2" />
                </div>
              ))}
            </div>

            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700/50 space-y-4">
              <h3 className="font-semibold text-amber-400">📝 Analyse</h3>
              <p className="text-slate-300">{defenseAnalysis.analyse}</p>
              
              {defenseAnalysis.pointsForts.length > 0 && (
                <div>
                  <p className="text-green-400 font-semibold mb-2">✓ Points forts</p>
                  <ul className="space-y-1">
                    {defenseAnalysis.pointsForts.map((point, i) => (
                      <li key={i} className="text-slate-400 text-sm">• {point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {defenseAnalysis.pointsFaibles.length > 0 && (
                <div>
                  <p className="text-red-400 font-semibold mb-2">✗ Points à améliorer</p>
                  <ul className="space-y-1">
                    {defenseAnalysis.pointsFaibles.map((point, i) => (
                      <li key={i} className="text-slate-400 text-sm">• {point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <Button 
              onClick={startInterrogation}
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold py-6 text-lg rounded-xl"
            >
              <Gavel className="w-5 h-5 mr-2" />
              Passer à l'interrogatoire
            </Button>
          </div>
        )}

        {/* Interrogation with Recording */}
        {phase === 'interrogation' && scenario && accusedPlayer && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm">
                <Gavel className="w-4 h-4" />
                Procureur IA
              </div>
              <h2 className="text-3xl font-bold">Interrogatoire</h2>
              <p className="text-slate-400">Question {questionIndex + 1} sur 3</p>
            </div>

            {/* Previous responses scores */}
            {interrogationResponses.length > 0 && (
              <div className="flex gap-2 justify-center">
                {interrogationResponses.map((r, i) => (
                  <div 
                    key={i} 
                    className={`px-3 py-1 rounded-full text-sm border ${r.analysis ? getScoreBgColor(r.analysis.score) : 'bg-slate-700/50 border-slate-600'}`}
                  >
                    Q{i + 1}: {r.analysis ? `${r.analysis.score}/100` : '—'}
                  </div>
                ))}
              </div>
            )}

            {/* Voice Toggle */}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setProsecutorVoiceEnabled(!prosecutorVoiceEnabled);
                  if (isSpeakingQuestion) {
                    stopSpeakingQuestion();
                  }
                }}
                className={`text-xs ${prosecutorVoiceEnabled ? 'text-red-400' : 'text-slate-500'}`}
              >
                {prosecutorVoiceEnabled ? (
                  <>
                    <Volume2 className="w-4 h-4 mr-1" />
                    Voix activée
                  </>
                ) : (
                  <>
                    <VolumeX className="w-4 h-4 mr-1" />
                    Voix désactivée
                  </>
                )}
              </Button>
            </div>

            <div className="bg-gradient-to-r from-slate-800/50 to-red-900/20 p-6 rounded-xl border border-red-500/30">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 ${isSpeakingQuestion ? 'animate-pulse' : ''}`}>
                  {isSpeakingQuestion ? (
                    <Volume2 className="w-6 h-6 text-red-400" />
                  ) : (
                    <Scale className="w-6 h-6 text-red-400" />
                  )}
                </div>
                <div className="flex-1">
                  {isGeneratingQuestion ? (
                    <div className="flex items-center gap-3 text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Le procureur prépare sa question...
                    </div>
                  ) : currentQuestion ? (
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xl text-white font-medium">
                          "{currentQuestion.question}"
                        </p>
                        {prosecutorVoiceEnabled && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (isSpeakingQuestion) {
                                stopSpeakingQuestion();
                              } else {
                                speakQuestion(currentQuestion.question);
                              }
                            }}
                            className="flex-shrink-0 text-red-400 hover:text-red-300"
                          >
                            {isSpeakingQuestion ? (
                              <VolumeX className="w-5 h-5" />
                            ) : (
                              <Volume2 className="w-5 h-5" />
                            )}
                          </Button>
                        )}
                      </div>
                      {currentQuestion.followUp && (
                        <p className="text-slate-400 italic text-sm">
                          Relance possible : "{currentQuestion.followUp}"
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Response Recording */}
            <div className="bg-slate-800/50 p-6 rounded-xl border border-purple-500/30 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-slate-400 text-sm">Réponse de {accusedPlayer.name}</p>
                {currentResponseAnalysis && (
                  <span className={`px-3 py-1 rounded-full text-sm border ${getScoreBgColor(currentResponseAnalysis.score)}`}>
                    {currentResponseAnalysis.score}/100
                  </span>
                )}
              </div>
              
              {!isRecordingResponse && !currentResponseText && !isTranscribingResponse && !isAnalyzingResponse && (
                <div className="flex flex-col items-center gap-3">
                  <Button
                    onClick={startRecordingResponse}
                    className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-lg shadow-purple-500/30"
                  >
                    <Mic className="w-8 h-8" />
                  </Button>
                  <p className="text-slate-500 text-sm">Enregistrer la réponse (optionnel)</p>
                </div>
              )}
              
              {isRecordingResponse && (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-20 h-20 rounded-full bg-red-500/20 border-4 border-red-500 flex items-center justify-center animate-pulse">
                    <div className="text-center">
                      <Mic className="w-6 h-6 text-red-400 mx-auto" />
                      <span className="text-lg font-mono font-bold text-red-400">{formatTime(responseRecordingTime)}</span>
                    </div>
                  </div>
                  <Button
                    onClick={handleStopAndTranscribeResponse}
                    className="bg-red-500 hover:bg-red-600 text-white px-6"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Arrêter
                  </Button>
                </div>
              )}
              
              {(isTranscribingResponse || isAnalyzingResponse) && (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-400" />
                  <p className="text-purple-400 text-sm">
                    {isTranscribingResponse ? 'Transcription...' : 'Analyse IA...'}
                  </p>
                </div>
              )}
              
              {currentResponseText && (
                <div className="space-y-3">
                  <p className="text-slate-300 italic text-sm">"{currentResponseText}"</p>
                  {currentResponseAnalysis && (
                    <p className="text-slate-400 text-xs">{currentResponseAnalysis.feedback}</p>
                  )}
                </div>
              )}
            </div>

            <Button 
              onClick={nextQuestion}
              disabled={isGeneratingQuestion || isRecordingResponse || isTranscribingResponse || isAnalyzingResponse}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold py-6 text-lg rounded-xl"
            >
              {questionIndex < 2 ? (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Question suivante
                </>
              ) : (
                <>
                  <Vote className="w-5 h-5 mr-2" />
                  Passer au verdict
                </>
              )}
            </Button>
          </div>
        )}

        {/* Verdict */}
        {phase === 'verdict' && accusedPlayer && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-2">
              <Vote className="w-12 h-12 mx-auto text-amber-400" />
              <h2 className="text-3xl font-bold">Le Verdict</h2>
              <p className="text-slate-400">Le jury doit voter</p>
            </div>

            {/* AI Scores Summary */}
            {(defenseAnalysis || interrogationResponses.some(r => r.analysis)) && (
              <div className="bg-purple-500/10 p-4 rounded-xl border border-purple-500/30 space-y-3">
                <p className="text-purple-400 font-semibold text-center">📊 Scores IA</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {defenseAnalysis && (
                    <span className={`px-3 py-1 rounded-full text-sm border ${getScoreBgColor(defenseAnalysis.scoreGlobal)}`}>
                      Plaidoirie: {defenseAnalysis.scoreGlobal}
                    </span>
                  )}
                  {interrogationResponses.map((r, i) => r.analysis && (
                    <span key={i} className={`px-3 py-1 rounded-full text-sm border ${getScoreBgColor(r.analysis.score)}`}>
                      Q{i + 1}: {r.analysis.score}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              {players.filter(p => p.id !== accusedPlayer.id).map((player) => (
                <div 
                  key={player.id}
                  className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50"
                >
                  <p className="font-medium mb-3">{player.name}</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => castVote(player.id, 'innocent')}
                      variant={player.vote === 'innocent' ? 'default' : 'outline'}
                      className={player.vote === 'innocent' 
                        ? 'flex-1 bg-green-500 hover:bg-green-600 text-white' 
                        : 'flex-1 border-green-500/50 text-green-400 hover:bg-green-500/20'
                      }
                    >
                      ✓ Innocent
                    </Button>
                    <Button
                      onClick={() => castVote(player.id, 'guilty')}
                      variant={player.vote === 'guilty' ? 'default' : 'outline'}
                      className={player.vote === 'guilty' 
                        ? 'flex-1 bg-red-500 hover:bg-red-600 text-white' 
                        : 'flex-1 border-red-500/50 text-red-400 hover:bg-red-500/20'
                      }
                    >
                      ✕ Coupable
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Button 
              onClick={endGame}
              disabled={players.filter(p => p.id !== accusedPlayer.id).some(p => !p.vote)}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold py-6 text-lg rounded-xl"
            >
              <Gavel className="w-5 h-5 mr-2" />
              Rendre le verdict
            </Button>
          </div>
        )}

        {/* End */}
        {phase === 'end' && accusedPlayer && scenario && (
          <div className="space-y-6 animate-fade-in text-center">
            {(() => {
              const { guiltyVotes, innocentVotes, aiInfluence, averageAiScore, isGuilty } = calculateVerdict();
              return (
                <>
                  <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${isGuilty ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                    <Gavel className={`w-12 h-12 ${isGuilty ? 'text-red-400' : 'text-green-400'}`} />
                  </div>
                  
                  <div className="space-y-2">
                    <h2 className={`text-4xl font-black ${isGuilty ? 'text-red-400' : 'text-green-400'}`}>
                      {isGuilty ? 'COUPABLE' : 'INNOCENT'}
                    </h2>
                    <p className="text-xl text-slate-300">{accusedPlayer.name}</p>
                  </div>

                  <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700/50">
                    <h3 className="font-semibold text-amber-400 mb-4">Résultat du vote</h3>
                    <div className="flex justify-center gap-8">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-green-400">{innocentVotes}</p>
                        <p className="text-slate-400">Innocent</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-red-400">{guiltyVotes}</p>
                        <p className="text-slate-400">Coupable</p>
                      </div>
                    </div>
                    
                    {aiInfluence !== 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-700">
                        <p className="text-purple-400 text-sm">
                          <Brain className="w-4 h-4 inline mr-1" />
                          Influence IA (score moyen: {averageAiScore}/100) : {aiInfluence > 0 ? `+${aiInfluence} innocent` : `+${Math.abs(aiInfluence)} coupable`}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* All AI scores */}
                  {(defenseAnalysis || interrogationResponses.some(r => r.analysis)) && (
                    <div className="bg-purple-500/10 p-4 rounded-xl border border-purple-500/30">
                      <p className="text-slate-400 text-sm mb-3">Détail des scores IA</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {defenseAnalysis && (
                          <span className={`px-3 py-1 rounded-full text-sm border ${getScoreBgColor(defenseAnalysis.scoreGlobal)}`}>
                            Plaidoirie: {defenseAnalysis.scoreGlobal}
                          </span>
                        )}
                        {interrogationResponses.map((r, i) => r.analysis && (
                          <span key={i} className={`px-3 py-1 rounded-full text-sm border ${getScoreBgColor(r.analysis.score)}`}>
                            Q{i + 1}: {r.analysis.score}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-amber-500/10 p-4 rounded-xl border border-amber-500/30">
                    <p className="text-slate-400 text-sm mb-1">L'affaire</p>
                    <p className="text-amber-400 font-semibold">{scenario.title}</p>
                  </div>
                </>
              );
            })()}

            <div className="flex gap-3 pt-4">
              <Button 
                onClick={resetGame}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Nouvelle partie
              </Button>
              <Link to="/" className="flex-1">
                <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold">
                  Retour à l'accueil
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
